import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUp,
  ArrowDown,
  Save,
  RefreshCw,
  Heart,
  CheckSquare,
  Calendar,
  MessageSquare,
  BookOpen,
  Barcode,
  Timer,
  Baby,
  TrendingUp,
  Moon,
  AudioLines,
  GripVertical,
  Eye,
  EyeOff,
  Shield,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';

const supabase = getSupabaseAdminClient();

// Tool definition with metadata
interface ToolDefinition {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
}

// Config entry as stored in DB
interface ToolMenuEntry {
  id: string;
  visible: boolean;
}

// All available tools with their metadata
const TOOL_DEFINITIONS: Record<string, ToolDefinition> = {
  'baby-care': {
    id: 'baby-care',
    label: 'Baby-Pflege',
    description: 'Füttern, Windeln, Schlaf tracken',
    icon: Baby,
  },
  'baby-growth': {
    id: 'baby-growth',
    label: 'Wachstum',
    description: 'Gewicht, Größe & Perzentilen',
    icon: TrendingUp,
  },
  'sleep-plan': {
    id: 'sleep-plan',
    label: 'Schlafplan',
    description: 'Wake Windows & Schlaf-Erinnerungen',
    icon: Moon,
  },
  'sleep-sounds': {
    id: 'sleep-sounds',
    label: 'Schlafklänge',
    description: 'Beruhigende Sounds zum Einschlafen',
    icon: AudioLines,
  },
  names: {
    id: 'names',
    label: 'Namensfinder',
    description: 'Euren Lieblingsnamen finden',
    icon: Heart,
  },
  checklists: {
    id: 'checklists',
    label: 'Checklisten',
    description: 'Alles im Blick',
    icon: CheckSquare,
  },
  calendar: {
    id: 'calendar',
    label: 'Kalender',
    description: 'Termine & Untersuchungen',
    icon: Calendar,
  },
  contractions: {
    id: 'contractions',
    label: 'Wehenzähler',
    description: 'Wehen tracken & auswerten',
    icon: Timer,
  },
  chat: {
    id: 'chat',
    label: 'KI-Assistent',
    description: 'Fragen schnell beantwortet',
    icon: MessageSquare,
  },
  guides: {
    id: 'guides',
    label: 'Behördenguide',
    description: 'Formulare einfach erklärt',
    icon: BookOpen,
  },
  produktcheck: {
    id: 'produktcheck',
    label: 'Produktcheck',
    description: 'Produkte & Kosmetik prüfen',
    icon: Barcode,
  },
};

// Default tools per mode (initial order)
const DEFAULT_PREGNANCY_TOOLS: ToolMenuEntry[] = [
  { id: 'names', visible: true },
  { id: 'checklists', visible: true },
  { id: 'calendar', visible: true },
  { id: 'contractions', visible: true },
  { id: 'chat', visible: true },
  { id: 'guides', visible: true },
  { id: 'produktcheck', visible: true },
];

const DEFAULT_BABY_TOOLS: ToolMenuEntry[] = [
  { id: 'baby-care', visible: true },
  { id: 'baby-growth', visible: true },
  { id: 'sleep-plan', visible: true },
  { id: 'sleep-sounds', visible: true },
  { id: 'checklists', visible: true },
  { id: 'calendar', visible: true },
  { id: 'chat', visible: true },
  { id: 'guides', visible: true },
  { id: 'produktcheck', visible: true },
];

type Mode = 'pregnancy' | 'baby';

function ToolList({
  items,
  onMove,
  onToggleVisibility,
}: {
  items: ToolMenuEntry[];
  onMove: (index: number, direction: 'up' | 'down') => void;
  onToggleVisibility: (index: number) => void;
}) {
  return (
    <div className="divide-y divide-slate-200">
      {items.map((item, index) => {
        const def = TOOL_DEFINITIONS[item.id];
        if (!def) return null;
        const Icon = def.icon;

        return (
          <div
            key={item.id}
            className={`px-6 py-4 flex items-center gap-4 transition-colors ${
              !item.visible ? 'bg-slate-50 opacity-60' : ''
            }`}
          >
            {/* Position indicator */}
            <div className="flex-shrink-0 text-slate-400">
              <GripVertical className="w-5 h-5" />
            </div>

            {/* Order number */}
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600">
              {index + 1}
            </div>

            {/* Icon */}
            <div className="flex-shrink-0 w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
              <Icon className="w-5 h-5 text-slate-600" />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-slate-900">{def.label}</h3>
              <p className="text-sm text-slate-500 truncate">{def.description}</p>
            </div>

            {/* Visibility toggle */}
            <button
              onClick={() => onToggleVisibility(index)}
              className="flex-shrink-0 focus:outline-none"
              title={item.visible ? 'Ausblenden' : 'Einblenden'}
            >
              {item.visible ? (
                <Eye className="w-5 h-5 text-teal-500" />
              ) : (
                <EyeOff className="w-5 h-5 text-slate-300" />
              )}
            </button>

            {/* Move buttons */}
            <div className="flex flex-col gap-1 flex-shrink-0">
              <button
                onClick={() => onMove(index, 'up')}
                disabled={index === 0}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Nach oben"
              >
                <ArrowUp className="w-4 h-4 text-slate-600" />
              </button>
              <button
                onClick={() => onMove(index, 'down')}
                disabled={index === items.length - 1}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Nach unten"
              >
                <ArrowDown className="w-4 h-4 text-slate-600" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ToolMenuPage() {
  const queryClient = useQueryClient();
  const [activeMode, setActiveMode] = useState<Mode>('pregnancy');
  const [pregnancyItems, setPregnancyItems] = useState<ToolMenuEntry[] | null>(null);
  const [babyItems, setBabyItems] = useState<ToolMenuEntry[] | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Load config from DB
  const { isLoading, refetch } = useQuery({
    queryKey: ['tool-menu-config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key, value')
        .in('key', ['tool_menu_pregnancy', 'tool_menu_baby']);

      if (error) throw error;

      const configMap: Record<string, ToolMenuEntry[]> = {};
      (data as { key: string; value: unknown }[])?.forEach((s) => {
        configMap[s.key] = s.value as ToolMenuEntry[];
      });

      const pItems = configMap['tool_menu_pregnancy'] || DEFAULT_PREGNANCY_TOOLS;
      const bItems = configMap['tool_menu_baby'] || DEFAULT_BABY_TOOLS;

      setPregnancyItems(pItems);
      setBabyItems(bItems);
      setHasChanges(false);

      return { pregnancy: pItems, baby: bItems };
    },
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async () => {
      const updates = [
        { key: 'tool_menu_pregnancy', value: pregnancyItems, description: 'Tool-Menü Reihenfolge und Sichtbarkeit im Schwangerschafts-Modus' },
        { key: 'tool_menu_baby', value: babyItems, description: 'Tool-Menü Reihenfolge und Sichtbarkeit im Baby-Modus' },
      ];

      for (const update of updates) {
        const { data: existing } = await supabase
          .from('system_settings')
          .select('key')
          .eq('key', update.key)
          .single();

        if (existing) {
          const { error } = await supabase
            .from('system_settings')
            .update({ value: update.value, updated_at: new Date().toISOString() } as never)
            .eq('key', update.key);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('system_settings')
            .insert({ key: update.key, value: update.value, description: update.description } as never);
          if (error) throw error;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tool-menu-config'] });
      setHasChanges(false);
      toast.success('Tool-Menü gespeichert');
    },
    onError: () => {
      toast.error('Fehler beim Speichern');
    },
  });

  const currentItems = activeMode === 'pregnancy' ? pregnancyItems : babyItems;
  const setCurrentItems = activeMode === 'pregnancy' ? setPregnancyItems : setBabyItems;

  const handleMove = (index: number, direction: 'up' | 'down') => {
    if (!currentItems) return;
    const newItems = [...currentItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;

    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setCurrentItems(newItems);
    setHasChanges(true);
  };

  const handleToggleVisibility = (index: number) => {
    if (!currentItems) return;
    const newItems = [...currentItems];
    newItems[index] = { ...newItems[index], visible: !newItems[index].visible };
    setCurrentItems(newItems);
    setHasChanges(true);
  };

  const handleReset = () => {
    if (activeMode === 'pregnancy') {
      setPregnancyItems([...DEFAULT_PREGNANCY_TOOLS]);
    } else {
      setBabyItems([...DEFAULT_BABY_TOOLS]);
    }
    setHasChanges(true);
  };

  const visibleCount = currentItems?.filter((i) => i.visible).length ?? 0;
  const totalCount = currentItems?.length ?? 0;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Tool-Menü</h1>
            <p className="text-slate-600">
              Reihenfolge und Sichtbarkeit der Tools auf der /tools Seite verwalten
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => refetch()}
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
              title="Aktualisieren"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Mode Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveMode('pregnancy')}
          className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeMode === 'pregnancy'
              ? 'bg-teal-500 text-white shadow-sm'
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          }`}
        >
          Schwangerschafts-Modus
        </button>
        <button
          onClick={() => setActiveMode('baby')}
          className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activeMode === 'baby'
              ? 'bg-teal-500 text-white shadow-sm'
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          }`}
        >
          Baby-Modus
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-500">Lädt Konfiguration...</div>
        </div>
      ) : currentItems ? (
        <>
          {/* Stats bar */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-slate-500">
              {visibleCount} von {totalCount} Tools sichtbar
            </div>
            <button
              onClick={handleReset}
              className="text-sm text-slate-500 hover:text-slate-700 underline"
            >
              Auf Standard zurücksetzen
            </button>
          </div>

          {/* Tool list */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <ToolList
              items={currentItems}
              onMove={handleMove}
              onToggleVisibility={handleToggleVisibility}
            />
          </div>

          {/* Save button */}
          {hasChanges && (
            <div className="mt-6 flex items-center gap-4">
              <button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                className="flex items-center gap-2 px-6 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 font-medium shadow-sm"
              >
                <Save className="w-5 h-5" />
                {saveMutation.isPending ? 'Speichert...' : 'Änderungen speichern'}
              </button>
              <button
                onClick={() => refetch()}
                className="px-4 py-3 text-slate-600 hover:text-slate-800 transition-colors"
              >
                Verwerfen
              </button>
            </div>
          )}
        </>
      ) : null}

      {/* Info */}
      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-amber-800">Hinweis</h3>
            <p className="text-sm text-amber-700 mt-1">
              Änderungen wirken sich sofort auf alle Nutzer aus. Ausgeblendete Tools sind
              für Nutzer nicht mehr auf der Tools-Seite sichtbar. Die Feature-Toggles
              unter &quot;Verwaltung &gt; Feature-Toggles&quot; haben weiterhin Vorrang – ein
              deaktiviertes Feature wird unabhängig von dieser Einstellung nicht angezeigt.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
